import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import {
    type BrowseDimensionPair,
    BROWSE_DIMENSION_PATH_TO_FACET_TYPE,
    BROWSE_SEMANTIC_FACET_IDS_MERGED_INTO_CATEGORIES,
} from '@/utils/browsePath/browsePath';
import { pruneCategoryIdQueryParamForBrowseSeoPath } from '@/utils/browsePath/buildCategoryBrowseNavigateUrl';
import { buildSearchQuery } from '@/utils/search/searchQuery';

export const DEFAULT_PRICE_GUIDE_SORT = SearchSortAndDirection.TimeNewest;

export const CATEGORY_ID_BACKED_BROWSE_FACET_TYPES = BROWSE_SEMANTIC_FACET_IDS_MERGED_INTO_CATEGORIES;

export const mapResolvedCategoryIdsToBrowseFacetOptions = (
    pathPairs: BrowseDimensionPair[],
    resolvedCategoryIds: number[] | undefined
): Record<string, string[]> | undefined => {
    if (!resolvedCategoryIds || resolvedCategoryIds.length === 0 || pathPairs.length === 0) {
        return undefined;
    }

    const resolvedFacetIds = resolvedCategoryIds.slice(1);
    if (resolvedFacetIds.length < pathPairs.length) {
        return undefined;
    }

    const alignedFacetIds = resolvedFacetIds.slice(resolvedFacetIds.length - pathPairs.length);
    const out: Record<string, string[]> = {};
    pathPairs.forEach((pair, idx) => {
        const facetType = BROWSE_DIMENSION_PATH_TO_FACET_TYPE[pair.dimensionKey];
        if (!facetType || !CATEGORY_ID_BACKED_BROWSE_FACET_TYPES.has(facetType)) {
            return;
        }
        const resolvedId = alignedFacetIds[idx];
        if (resolvedId && !Number.isNaN(resolvedId)) {
            out[facetType] = [String(resolvedId)];
        }
    });

    return Object.keys(out).length > 0 ? out : undefined;
};

export const mergeCategoryIdsIntoQueryParams = (
    query: Record<string, any>,
    categoryIds: string[]
): Record<string, any> => {
    if (categoryIds.length === 0) {
        return query;
    }

    const existingCategoryIds = String(query.category_id ?? '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
    const mergedCategoryIds = [...new Set([...existingCategoryIds, ...categoryIds.map(String)])];

    return {
        ...query,
        category_id: mergedCategoryIds.join(','),
    };
};

export const getBrowseCategoryIdValues = (browseFacetOptionIds: Record<string, string[]> | undefined) =>
    Object.entries(browseFacetOptionIds ?? {}).flatMap(([facetType, ids]) =>
        CATEGORY_ID_BACKED_BROWSE_FACET_TYPES.has(facetType) ? ids : []
    );

type BuildCategorySearchQueryArgs = {
    browseFacetOptionIds: Record<string, string[]> | undefined;
    defaultSortOrder: SearchSortAndDirection;
    pathname: string;
    queryParams: Record<string, any>;
    rawFacets: any;
    searchPageSize: number;
};

export const buildCategorySearchQuery = ({
    browseFacetOptionIds,
    defaultSortOrder,
    pathname,
    queryParams,
    rawFacets,
    searchPageSize,
}: BuildCategorySearchQueryArgs) => {
    const queryParamsForSearch = pruneCategoryIdQueryParamForBrowseSeoPath(pathname, queryParams, rawFacets);
    const browseCategoryIdValues = getBrowseCategoryIdValues(browseFacetOptionIds);
    const queryParamsWithBrowseCategoryIds = mergeCategoryIdsIntoQueryParams(
        queryParamsForSearch,
        browseCategoryIdValues
    );

    const searchQuery = buildSearchQuery({
        browseFacetOptionIds,
        defaultSortOrder,
        pathname,
        queryParams: queryParamsWithBrowseCategoryIds,
        searchPageSize,
    });

    return {
        queryParamsForSearch,
        queryParamsWithBrowseCategoryIds,
        searchQuery,
    };
};
